/**
 * 两个纯几何组件（P1-1.7）
 *
 * 都不带判定语义：柱子高低是"占峰值多少"，带子宽度是"P25 到 P75 占轴多宽"。
 * 什么时候算"稳定"、什么时候算"达标"，只看服务端给的桶码（D12）。
 *
 * 本文件不出现任何中文串（buildChecks 扫 v2/*.jsx）：图上的每个字都来自 copy.js 的 LABEL，
 * 改文案因此只需要动一个文件（R15）。
 */
import React from 'react';
import PropTypes from 'prop-types';
import { Tooltip } from 'antd';
import { weekSlots, bandGeometry, markerPct, levelOf, maxOf } from './scale';
import { LABEL, formatNumber } from './copy';
import styles from './index.less';

/**
 * 6 个自然周的达成度。
 * `null` 槽（这一周没被窗口完整覆盖）画成斜纹 + 低高度的空条，**不**画成 0 高度：
 * 那正是 V1 六周折线被指出的毛病——缺的周被当成"这周没做"（决策 ⑨/D15）。
 */
export const WeeklyBars = React.memo(function WeeklyBars({ values, unit }) {
  const slots = weekSlots(values);
  if (!slots.length) return null;
  return (
    <div className={styles.weekly} data-test="hh-v2-weekly">
      {slots.map((slot) => (
        <Tooltip
          key={slot.index}
          title={
            slot.available
              ? LABEL.weekValue(slot.index, slot.value, unit)
              : LABEL.weekVoid(slot.index)
          }
        >
          <span className={styles.weekCol}>
            <span
              className={`${styles.weekBar} ${
                slot.available ? '' : styles.weekBarVoid
              }`}
              style={{
                height:
                  slot.available && slot.heightPct !== null
                    ? `${slot.heightPct}%`
                    : '4px',
              }}
            />
          </span>
        </Tooltip>
      ))}
    </div>
  );
});

/**
 * 个人基线带（P25–P75）。缺任意一端就不画带子 —— 留白比画半条带子诚实（D11）。
 * 轴峰值取 p75：带子和中线因此一定在同一把尺上（scale.js 只做缩放，不做判定）。
 */
export const BaselineBand = React.memo(function BaselineBand({
  p25,
  p50,
  p75,
  unit,
}) {
  const peak =
    p25 === null || p25 === undefined || p75 === null || p75 === undefined
      ? null
      : p75;
  if (peak === null) {
    return <div className={styles.bandEmpty}>{LABEL.baselineEmpty}</div>;
  }
  const band = bandGeometry({ p25, p75, axisPeak: peak });
  const marker = markerPct(p50, peak);
  return (
    <div className={styles.band} data-test="hh-v2-band">
      <span className={styles.bandAxis} />
      <span
        className={styles.bandBox}
        style={{ left: `${band.leftPct}%`, width: `${band.widthPct}%` }}
      />
      {marker !== null ? (
        <span className={styles.bandMedian} style={{ left: `${marker}%` }} />
      ) : null}
      <span className={styles.bandLabelLeft}>
        {LABEL.baselineP('P25', p25, unit)}
      </span>
      <span className={styles.bandLabelRight}>
        {LABEL.baselineP('P75', p75, unit)}
      </span>
    </div>
  );
});

WeeklyBars.propTypes = {
  // 槽位可以是 null（未覆盖），所以不能写 arrayOf(number)：
  // 那会在每次渲染刷一串 propType 告警，把一个正常的三态当成类型错误。
  values: PropTypes.array, // eslint-disable-line react/forbid-prop-types
  unit: PropTypes.string,
};
WeeklyBars.defaultProps = { values: [], unit: '' };

BaselineBand.propTypes = {
  p25: PropTypes.number,
  p50: PropTypes.number,
  p75: PropTypes.number,
  unit: PropTypes.string,
};
BaselineBand.defaultProps = { p25: null, p50: null, p75: null, unit: '' };

const STRIP_LEVEL = [
  styles.stripLv0,
  styles.stripLv1,
  styles.stripLv2,
  styles.stripLv3,
];

/** 42 天按 7 天切一段（观察窗恒为 42 天 ⇒ 6 段） */
function chunkByWeek(days) {
  const out = [];
  for (let i = 0; i < days.length; i += 7) {
    out.push(days.slice(i, i + 7));
  }
  return out;
}

/**
 * 42 天一致性条（每 7 天一组）—— 这张卡的“习惯底噪”
 *
 * 一格一天：有记录的那天按**该指标自己窗口峰值**上深浅色，没有记录留浅底。
 * 读法就是 GitHub contributions 那一套：一眼看出“这段到底坚持了没、断在哪一段”。
 * 它不是用来读精确值的（精确值看左右两侧的统计数字），而是用 3 秒回答“有规律吗”。
 *
 * 没有 echarts，也不需要一个：42 个 span 就够了，而且单测里照样能渲染。
 */
export const DayStrip = React.memo(function DayStrip({ days, unit }) {
  const list = Array.isArray(days) ? days : [];
  const peak = maxOf(list.map((day) => day.value));
  const groups = chunkByWeek(list);
  return (
    <div className={styles.strip} data-test="hh-v2-strip">
      {groups.map((group, groupIndex) => (
        <div
          className={styles.stripGroup}
          key={group[0] ? group[0].date : groupIndex}
        >
          {group.map((day) => {
            const level = day.value === null ? null : levelOf(day.value, peak);
            const detail =
              day.value === null
                ? LABEL.noRecord
                : `${formatNumber(day.value)} ${unit || ''}`.trim();
            return (
              <Tooltip
                key={day.date || detail}
                title={LABEL.dayBarTip(day.date, detail)}
              >
                <span
                  className={`${styles.stripCell} ${
                    level === null ? styles.stripOff : STRIP_LEVEL[level]
                  }`}
                />
              </Tooltip>
            );
          })}
        </div>
      ))}
    </div>
  );
});

DayStrip.propTypes = {
  // [{date, value}]，value 为 null 表示这天没有记录（series.dayBuckets 的产物）
  days: PropTypes.array, // eslint-disable-line react/forbid-prop-types
  unit: PropTypes.string,
};

DayStrip.defaultProps = { days: [], unit: '' };
